<template>
    <div class="">
      <div v-if="useStartEndInterval" class="grid grid-cols-1 lg:grid-cols-1 my-2 text-center">
        <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
          <label for="dp-input-start_date">Start Date</label>
          <date-time-picker   id="start_date"
                              v-model="$data._startDate"
                              :format="format"
                              :view-mode="viewMode"
                              :min-date="minDate"
                              :max-date="maxDate"
                              :side-by-side="true"
                              :timezone="timezone"
          ></date-time-picker>
        </div>

        <!-- End Date -->
        <div class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
          <label for="dp-input-end_date">End Date</label>
          <date-time-picker   id="end_date"
                              v-model="$data._endDate"
                              :format="format"
                              :view-mode="viewMode"
                              :min-date="minDate"
                              :max-date="maxDate"
                              :side-by-side="true"
                              :timezone="timezone"
          ></date-time-picker>
        </div>
      </div>
        <div v-else class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
            <label for="dp-input-month">{{ interval === 'monthly' ? 'Month' : 'End of Interval' }}</label>
            <date-time-picker   id="month"
                                v-model:model-value="$data._endOfInterval"
                                :format="format"
                                :view-mode="viewMode"
                                :min-date="minDate"
                                :max-date="maxDate"
                                :timezone="timezone"
            ></date-time-picker>
        </div>

        <!-- which half month to report on during semimonths -->
        <div    v-if="interval === 'semi-monthly'"
                class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
            <span>Half of Month Reported on</span>
            <select2 :options="semiMonthIntervals"
                     v-model:value="$data._semiMonthHalf"
                     :close-on-select="true"
            ></select2>
        </div>

        <div   v-if="useMeters"
          class="grid grid-cols-1 lg:grid-cols-2 my-2 text-center">
            <label for="selected_meters[]">Select One or More Points</label>
            <select2    id="selected_meters[]"
                        :multiple="true"
                        :options="meters"
                        v-model:value="$data._selectedMeters"
                        :allow-clear="true"
            ></select2>
        </div>

        <div class="flex justify-center">
            <button v-if="isRunning"
                    class="col-span-2 w-full lg:w-2/3 my-4 relative inline-flex items-center justify-center
                            px-4 py-2 border border-transparent text-sm bg-blue-600 text-white font-medium py-2 px-4
                            rounded opacity-50 cursor-not-allowed">
                <span class="lnr icon-loading animate-spin mr-3"></span>
                Running Report, Please Wait
            </button>
            <button v-else-if="$data._selectedMeters.length === 0 && useMeters"
                    class="col-span-2 w-full lg:w-2/3 my-4 relative inline-flex items-center justify-center
                            px-4 py-2 border border-transparent text-sm bg-blue-600 text-white font-medium py-2 px-4
                            rounded opacity-50 cursor-not-allowed">
                Please Select At Least One Meter
            </button>
            <button v-else
                    class="col-span-2 w-full lg:w-2/3 my-4 relative inline-flex items-center justify-center px-4 py-2 border
                            border-transparent text-sm leading-5 font-medium rounded-md text-white bg-blue-600
                            hover:bg-blue-500 focus:outline-none focus:ring-blue-200 focus:border-blue-700
                            active:bg-blue-700"
                    @click="runReport">
                Run Report
            </button>
        </div>

    </div>
</template>

<script>
import axios from "axios"
import {companyMixin} from '@/mixins/companyMixin'
import {permissionMixin} from '@/mixins/permissionMixin'
import {iconNamesMixin} from '@/mixins/IconNamesMixin';

import DateTimePicker from "@/components/Reports/VueDatePicker";
import Select2 from "@/components/elements/select2";
import {Intervals} from "@/components/GroupReports/Intervals";

export default {
    name: "MeterIntervalSelection",
    mixins: [companyMixin, permissionMixin, iconNamesMixin],
    components: {DateTimePicker, Select2},
    props: {
        meters: {
            type: Object,
            default: [],
        },
        interval: {
            type: String,
            default: '',
        },
        endOfInterval: {
            type: Date,
        },
        minDate: {
            type: [Date, String],
        },
        maxDate: {
            type: [Date, String],
        },
        startDate: {
            type: [Date, String],
        },
        endDate: {
            type: [Date, String],
        },
        selectedMeters: {
            type: Array,
        },
        semiMonthHalf: {
            type: String,
            default: '',
        },
        useStartEndInterval: {
          type: Boolean,
          default: false
        },
        useMeters: {
          type: Boolean,
          default: true
        },
        timezone: {
          type: String,
          default: 'America/New_York'
        },
    },
    data() {
        return {
            isRunning: false,
            semiMonthIntervals: {
                first: '1st Half (1 - 15)',
                last: '2nd Half (16 - End of Month)',
            },

            formatDictionary: {
                monthly: 'MMMM YYYY',
                'semi-monthly': 'MMMM YYYY',
                daily: 'MMMM DD, YYYY',
                secondly: 'MMMM DD, YYYY  HH:mm',
            },
            viewModeDictionary: {
                monthly: 'months',
                'semi-monthly': 'months',
                daily: 'days',
                secondly: 'days',
            },

            format: null,
            viewMode: null,

            _selectedMeters: this.selectedMeters,
            _startDate: this.startDate,
            _endDate: this.endDate,

            _semiMonthHalf: new Date().getDate() <= 15 ? 'first' : 'last',
            _endOfInterval: new Date(),
        }
    },
    created() {
        this.format = this.getUpdatedFormat()
        this.viewMode = this.getViewMode()
    },
    mounted() {

    },
    methods: {
        runReport() {
            this.$emit('run-report')
        },
        fixDateForSemiMonthHalf() {
            // If the interval is not semi-monthly, return
            if (this.interval !== Intervals.SEMI_MONTHLY) {
                return
            }

            if (this.$data._semiMonthHalf === 'first') {
                this.$data._endOfInterval.setDate(1)
            }
            else {
                this.$data._endOfInterval.setDate(16)
            }

            this.$emit('update:endOfInterval', this.$data._endOfInterval)
        },
        updateSemiMonthHalf() {
            this.$data._semiMonthHalf = this.$data._endOfInterval.getDate() <= 15 ? 'first' : 'last'
        },
        getUpdatedFormat() {
            if (_.indexOf(_.keys(this.formatDictionary), this.interval) === -1) {
                return 'MMMM DD, YYYY'
            }
            return this.formatDictionary[this.interval]
        },
        getViewMode() {
            if (_.indexOf(_.keys(this.viewModeDictionary), this.interval) === -1) {
                return 'days'
            }
            return this.viewModeDictionary[this.interval]
        },
    },
    computed: {},
    watch: {
        selectedMeters: {
            handler (val, old) {
                if (val !== old) {
                    this.$data._selectedMeters = val
                }
            },
            deep: true
        },
        _selectedMeters: {
            handler(value, oldValue) {
                let allIndex = value.indexOf('all')
                if (allIndex !== -1) {
                    let newSelectedMeters = Object.keys(this.meters)
                    let metersAllIndex = newSelectedMeters.indexOf("all")
                    newSelectedMeters.splice(metersAllIndex, 1)
                    this.$data._selectedMeters = newSelectedMeters

                    this.$emit('update:selectedMeters', this.$data._selectedMeters)
                }

                this.$emit('update:selectedMeters', this.$data._selectedMeters)
            }, deep: true
        },
        startDate (val, old) {
          if (val !== old) {
            this.$data._startDate = val
          }
        },
        _startDate(val, old) {
          if (val !== old) {
            this.$emit('update:startDate', this.$data._startDate)
          }
        },
        endDate (val, old) {
          if (val !== old) {
            this.$data._endDate = val
          }
        },
        _endDate(val, old) {
          if (val !== old) {
            this.$emit('update:endDate', this.$data._endDate)
          }
        },
        _semiMonthHalf(val, old) {
            if (val !== old) {
                this.$emit('update:semiMonthHalf', val)
                this.fixDateForSemiMonthHalf()
            }
        },
        endOfInterval (val, old) {
            if (val !== old) {
                this.$data._endOfInterval = val
            }
        },
        _endOfInterval(val, old) {
            if (val !== old) {
                this.$emit('update:endOfInterval', this.$data._endOfInterval)
                this.updateSemiMonthHalf()
            }
        },
        interval() {
            this.format = this.getUpdatedFormat()
            this.viewMode = this.getViewMode()
            this.$emit('update:endOfInterval', this.$data._endOfInterval)
            this.updateSemiMonthHalf()
        },
    },
}
</script>

<style scoped>

</style>